import { BaseService } from './base';
import { Info, Plan, Panel, PanelAction } from '../models/auth';
import { ApiRequest } from '../lib/request';
import { ApiResponse, ApiListResponse } from '../lib/response';
import { UAParser } from 'ua-parser-js';

import type {
  AuthDomain,
  AuthResponse,
  PlanInfo,
  AuthLoginParams,
  AuthCodeLoginParams,
  AuthTokenLoginParams,
  AuthResetPasswordParams,
  AuthChangePasswordParams,
  AuthChangePinParams,
  AuthRequestCodeParams,
  AuthAccountsParams,
  AuthTokenFromAuthorizationCodeParams,
  AuthTokenFromRefreshTokenParams,
  AuthVerifyEmailParams,
  AuthLocationSummary,
} from '../types/interfaces/auth';

export class AuthService extends BaseService<Info> {
  override model = Info;
  override modelName = 'auth';
  override modelLabel = 'Auth';

  static device(user_agent?: string): string {
    const parser = UAParser(user_agent);
    const agent = parser as { os: { name?: string; version?: string }; browser: { name?: string; major?: string } };
    const device_name = `${agent.os.name || ''} ${agent.os.version || ''} (${agent.browser.name || ''} ${agent.browser.major || ''})`;
    return device_name;
  }

  // Custom request methods matching the old API behavior
  private async requestAuth<T>(request: ApiRequest): Promise<ApiResponse<T>> {
    const response = await this.request(request);
    
    if (!response.errors) {
      return response as ApiResponse<T>;
    } else {
      throw response.errors;
    }
  }

  private async requestSuccess(request: ApiRequest): Promise<boolean> {
    const response = await this.request(request);
    
    if (response.success) {
      return response.success;
    } else {
      throw response.errors;
    }
  }

  private async requestData<T>(request: ApiRequest): Promise<T> {
    const response = await this.request(request);
    
    if (!response.errors) {
      return response.data;
    } else {
      throw response.errors;
    }
  }

  async domain(domain: string): Promise<AuthDomain> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/domain`,
      query: { domain },
    });

    return await this.requestData<AuthDomain>(request);
  }

  async locations(ids: Array<string | number>): Promise<AuthLocationSummary[]> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/locations`,
      data: { ids },
      authenticated: false,
    });

    const response = await this.request(request);

    if (Array.isArray(response)) {
      return response;
    }

    if (response.errors) {
      throw response.errors;
    }

    return response.data || [];
  }

  async login(params: AuthLoginParams): Promise<ApiResponse<AuthResponse>> {
    const device_name = AuthService.device();

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/login`,
      data: {
        ...params,
        device_name,
      },
    });

    return await this.requestAuth<AuthResponse>(request);
  }

  async logout(): Promise<boolean> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/logout`,
    });

    return await this.requestSuccess(request);
  }

  async requestCode(params: AuthRequestCodeParams): Promise<any> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/requestCode`,
      data: params,
    });

    return await this.requestData(request);
  }

  async codeLogin(params: AuthCodeLoginParams): Promise<ApiResponse<AuthResponse>> {
    const device_name = AuthService.device();

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/codeLogin`,
      data: {
        ...params,
        device_name,
      },
    });

    return await this.requestAuth<AuthResponse>(request);
  }

  async tokenLogin(params: AuthTokenLoginParams): Promise<ApiResponse<AuthResponse>> {
    const device_name = AuthService.device();

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/tokenLogin`,
      data: {
        ...params,
        device_name,
      },
    });

    return await this.requestAuth<AuthResponse>(request);
  }

  async accounts(params: AuthAccountsParams): Promise<any> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/accounts`,
      data: params,
    });

    return await this.requestData(request);
  }

  async resetPassword(params: AuthResetPasswordParams): Promise<boolean> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/resetPassword`,
      data: params,
    });

    return await this.requestSuccess(request);
  }

  async changePassword(params: AuthChangePasswordParams): Promise<boolean> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/changePassword`,
      data: {
        old_password: params.oldPassword,
        new_password: params.newPassword,
        new_confirm: params.confirmPassword,
        revokeSessions: params.revokeSessions || false,
      },
    });

    return await this.requestSuccess(request);
  }

  async changePin(params: AuthChangePinParams): Promise<boolean> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/changePin`,
      data: {
        password: params.password,
        pin: params.newPin,
        confirm: params.confirmPin,
      },
    });

    return await this.requestSuccess(request);
  }

  async tokenFromAuthorizationCode(params: AuthTokenFromAuthorizationCodeParams): Promise<ApiResponse<AuthResponse>> {
    const device_name = AuthService.device(params.user_agent);

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/token`,
      data: {
        grant_type: 'authorization-code',
        client_id: params.client_id,
        redirect_uri: params.redirect_uri,
        code: params.code,
        scope: params.scope,
        device_name,
      },
    });

    return await this.requestAuth<AuthResponse>(request);
  }

  async tokenFromRefreshToken(params: AuthTokenFromRefreshTokenParams): Promise<AuthResponse> {
    const device_name = AuthService.device(params.user_agent);

    const formData = new URLSearchParams();
    formData.append('grant_type', 'refresh-token');
    formData.append('client_id', params.client_id);
    formData.append('refresh_token', params.refreshToken);
    formData.append('scope', params.scope);
    formData.append('device_name', device_name);

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/token`,
      data: formData,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      authenticated: false,
    });

    return await this.requestAuth<AuthResponse>(request);
  }

  async checkPin(pin: string): Promise<boolean> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/checkPin`,
      query: { pin },
    });

    return await this.requestSuccess(request);
  }

  async info(): Promise<ApiResponse<Info>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/info`,
    });

    return await this.requestItem<Info>(request, Info);
  }

  async plans(): Promise<PlanInfo[]> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/plans`,
    });

    // This returns raw data array, not a model list
    return await this.requestData<PlanInfo[]>(request);
  }

  async resendVerificationEmail(): Promise<any> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/resendVerificationEmail`,
    });

    return await this.requestData(request);
  }

  async verifyEmail(params: AuthVerifyEmailParams): Promise<boolean> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/verifyEmail`,
      query: {
        location: params.location,
        code: params.code,
      },
    });

    return await this.requestSuccess(request);
  }
} 
